/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { useInvoiceCreate } from "../../lib/react-query/useInvoiceCreate";
import { useInvoiceAddLine } from "../../lib/react-query/useInvoiceAddLine";
import { invalidateClientTerms } from "../../lib/react-query/invalidateClientTerms";
import { useCart } from "../useCart";
import { useOrderLines } from "./useOrderLines";
import { useDirectOrder } from "../useDirectOrder";
import { useAuth } from "../useAuth";
import { useBooking } from "../useBooking";
import { updateAxiosHeaders } from "../../axios.init";
import { ROUTES } from "../../constants/constants";
import { FlowType, setFlow } from "../../store/slices/flowSlice";

export const useCheckoutCart = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { setInvoice, clearLines, clearEntryRoute } = useCart();
  const { lines, isDirectOrder } = useOrderLines();
  const { clearDirectOrder } = useDirectOrder();
  const { fetchClientDetails, token, client } = useAuth();
  const { bookingRequest } = useBooking();

  const createInvoice = useInvoiceCreate();
  const addLine = useInvoiceAddLine();

  const isPending = createInvoice.isPending || addLine.isPending;
  const error = createInvoice.error || addLine.error;

  const cartCheckout = async ({ client: guestClient } = {}) => {
    if (!lines.length) return null;

    const hasTerms = bookingRequest?.terms && Object.keys(bookingRequest.terms).length > 0;
    const createPayload = {
      ...(guestClient ? { client: guestClient } : {}),
      ...(hasTerms ? { terms: bookingRequest.terms } : {}),
    };
    const created = await createInvoice.mutateAsync(createPayload);

    if (created?.token) {
      updateAxiosHeaders({ "X-Token": created.token });

      if (created.token !== token || !client) {
        await fetchClientDetails();
      }
    }

    if (hasTerms) {
      invalidateClientTerms(queryClient, bookingRequest.terms);
    }

    let latestInvoice = created;

    for (const line of lines) {
      latestInvoice = await addLine.mutateAsync({
        type: line.type,
        invoiceId: created.id,
        ...line.params,
      });
    }

    setInvoice(latestInvoice);
    if (isDirectOrder) {
      clearDirectOrder();
    } else {
      clearLines();
      clearEntryRoute();
    }
    dispatch(setFlow(FlowType.CART));
    navigate(ROUTES.CHECKOUT);

    return latestInvoice;
  };

  return { cartCheckout, isPending, error };
};
